import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Pack pre-rendered DOM elements into a multi-page A4 PDF.
 * Sections flow continuously; page breaks only when the next section
 * would not fit in the remaining vertical space (sections are never split).
 *
 * @param {HTMLElement[]} elements
 * @param {{
 *   footerText?: string,
 *   marginX?: number,
 *   marginTop?: number,
 *   footerReserve?: number,
 *   sectionGap?: number,
 *   scale?: number,
 * }} [options]
 * @returns {Promise<Blob>}
 */
export async function packHtmlElementsToPdfBlob(elements, options = {}) {
  const {
    footerText = 'Powered by Prosperity Leaders',
    marginX = 10,
    marginTop = 10,
    footerReserve = 12,
    sectionGap = 3,
    scale = 2,
  } = options;

  const nodes = (elements || []).filter(Boolean);
  if (!nodes.length) {
    throw new Error('No sections to render into PDF');
  }

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const usableHeight = pageHeight - marginTop - footerReserve;
  const maxWidth = pageWidth - marginX * 2;

  const blocks = [];
  for (const node of nodes) {
    const canvas = await html2canvas(node, {
      scale,
      useCORS: true,
      backgroundColor: '#FFFFFF',
      // Skip cross-origin/third-party iframes (e.g. Turnstile) during document clone.
      ignoreElements: (el) => el.tagName === 'IFRAME',
    });
    const widthRatio = maxWidth / canvas.width;
    let imgWidth = canvas.width * widthRatio;
    let imgHeight = canvas.height * widthRatio;

    // Oversized single section: scale down to fit one full usable page.
    if (imgHeight > usableHeight) {
      const fitRatio = usableHeight / imgHeight;
      imgWidth *= fitRatio;
      imgHeight = usableHeight;
    }

    blocks.push({
      imgData: canvas.toDataURL('image/png'),
      imgWidth,
      imgHeight,
    });
  }

  let currentY = marginTop;
  let isFirstBlock = true;

  for (const block of blocks) {
    const wouldOverflow = currentY + block.imgHeight > marginTop + usableHeight;

    if (!isFirstBlock && wouldOverflow) {
      pdf.addPage();
      currentY = marginTop;
    }

    pdf.addImage(
      block.imgData,
      'PNG',
      marginX,
      currentY,
      block.imgWidth,
      block.imgHeight,
      undefined,
      'FAST',
    );
    currentY += block.imgHeight + sectionGap;
    isFirstBlock = false;
  }

  const totalPages = pdf.getNumberOfPages();
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    pdf.setPage(pageNumber);
    pdf.setFontSize(10);
    pdf.setTextColor(27, 76, 135);
    pdf.text(footerText, pageWidth / 2, pageHeight - 6, { align: 'center' });
  }

  return pdf.output('blob');
}

export async function blobToBase64(blob) {
  const arrayBuffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
